import React, { useCallback, useEffect, useState } from 'react'
import { SkeletonList } from '../../components/Skeleton'
import {
  BackLink,
  Card,
  ContentMaxWidth,
  Muted,
  PageHeading,
  PageSubheading,
  Pill,
  Row,
  Stack,
} from '../../layout/styledComponents'
import { usePatient } from '../../context/PatientContext'
import * as svc from '../../services/recordsService'
import { parseUtcDate } from '../../utils/dates'

function formatAmount(minor, currency = 'INR') {
  const major = (minor || 0) / 100
  if (currency === 'INR') return `₹${major.toLocaleString('en-IN')}`
  if (currency === 'USD') return `$${major.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
  return `${currency} ${major}`
}

export default function ReceiptsPage() {
  const { currentProfile } = usePatient()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    if (!currentProfile) return
    setLoading(true)
    try {
      const res = await svc.listReceipts(currentProfile)
      setData(res)
    } finally {
      setLoading(false)
    }
  }, [currentProfile])

  useEffect(() => {
    setData(null)
    refresh()
  }, [refresh])

  return (
    <ContentMaxWidth>
      <Stack $gap="20px">
        <BackLink to="/patient-home/records">Back to Records</BackLink>
        <div>
          <Pill>Records</Pill>
          <PageHeading style={{ marginTop: 12 }}>Receipts &amp; invoices</PageHeading>
          <PageSubheading>Download or print for insurance.</PageSubheading>
        </div>

        {loading && !data ? (
          <SkeletonList count={3} />
        ) : !data?.items?.length ? (
          <div
            style={{
              padding: '48px 24px',
              textAlign: 'center',
              border: '1px dashed rgba(31,41,55,0.18)',
              borderRadius: 20,
            }}
          >
            <p style={{ fontSize: 18, marginTop: 0, marginBottom: 8 }}>No receipts yet.</p>
            <Muted>Receipts for paid sessions and packs land here.</Muted>
          </div>
        ) : (
          <Stack $gap="10px">
            {data.items.map((r) => (
              <Card key={r.id}>
                <Row $between $gap="12px" $wrap>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 16 }}>
                      {formatAmount(r.amountMinor, r.currency)}
                    </div>
                    <Muted>
                      {r.description}
                      {r.practitionerName ? ` · ${r.practitionerName}` : ''}
                    </Muted>
                    <Muted style={{ marginTop: 4, fontSize: 12 }}>
                      {parseUtcDate(r.paidAt).toLocaleDateString([], {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                      {r.type === 'GST' ? ' · GST invoice' : ''}
                    </Muted>
                  </div>
                  <Row $gap="8px">
                    {r.downloadUrl && (
                      <a
                        href={r.downloadUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          textDecoration: 'none',
                          padding: '8px 14px',
                          border: '1px solid #E6E0D6',
                          borderRadius: 999,
                          color: '#1F3A5F',
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        Download
                      </a>
                    )}
                  </Row>
                </Row>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>
    </ContentMaxWidth>
  )
}
